import React, { useContext, useEffect, useRef, useState, useCallback } from "react";
// import noImage from "../../../../../assets/images/icon_upload.svg";
import noImage from "../../../../../../public/icons/edit_document.svg";
import privateKdf from "../../../../../../public/images/user-lock2.png";
import Button from "../../../Elements/Button";
import CustomTooltip from "../../../Elements/Tooltip";
import { Col, Row } from "react-bootstrap";
import styles from "./KdfListCard.module.css";
import LaserLinkModal from "../../../Elements/LaserLink/LaserLinkModal";
import { networkService } from "../../../config/axios";
import { sanitizeHtml } from "../../../../utils/sanitizeHtml";
import UserInfoContext from "../../../../utils/userInfoContext";
import TagChipEditor from "../../../CommonComponents/Tags/TagChipEditor";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faQuestion,
  faRectangleXmark,
  faLock,
  faPen,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import Dropdown from "react-bootstrap/Dropdown";

const KdfListCard = React.memo(({
  data,
  triggerUdateRecord,
  triggerUpdateRecordData,
  time_zone_list,
  forKey,
  triggerDeleteKDF,
  triggerCloneKDF,
  triggerMakePrivateEvent,
}) => {
  const [deletePDFActiveState, setDeletePDFActiveState] = useState(null);
  const [privatePDFActiveState, setPrivatePDFActiveState] = useState(null);
  const [laserLINKEditStatus, setLaserLINKEditStatus] = useState(null);
  const [laserLINKBatchStatus, setLaserLINKBatchStatus] = useState(false);
  const [editableTitle, setEditableTitle] = useState({
    status: false,
    value: data?.title,
    id: data?.id,
  });
  // Tracks the last successfully-saved title locally so the card can reflect
  // a save immediately without re-fetching the whole list (see handleOutsideOrEnter).
  const [savedTitle, setSavedTitle] = useState(data?.title);
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const { setShowNotifications } = useContext(UserInfoContext);
  const editableRef = useRef(null);
  const editGroupRef = useRef(null);
  const MAX_TAGS = 4;
  const totalLaserLinks = Number(data?.tracking_link_count ?? 0);
  const laserLinkLabel = totalLaserLinks === 1 ? "LaserLINK" : "LaserLINX";
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isStatsCollapsed, setIsStatsCollapsed] = useState(true);

  const [currentTags, setCurrentTags] = useState(data?.tags || []);
  const currentTagsRef = useRef(currentTags);
  const tagUpdateTimeoutRef = useRef(null);

  useEffect(() => {
    setCurrentTags(data?.tags || []);
  }, [data?.tags]);

  useEffect(() => {
    currentTagsRef.current = currentTags;
  }, [currentTags]);

  useEffect(() => {
    setSavedTitle(data?.title);
  }, [data?.title]);

  // Console log for Jump to Page feature tracking with element details
  useEffect(() => {
    const fetchAndLogJumpToPageElements = async () => {
      if (!data?.count_live_files || data.count_live_files === 0) return;

      try {
        // Fetch all elements for this KDF
        const response = await networkService(
          `docs/all-elements/${data?.id}.json`,
          "GET"
        );

        if (response?.data && typeof response.data === 'object') {
          const elements = Object.values(response.data);
          const jumpToPageElements = [];

          // Find all elements with Jump to Page actions
          elements.forEach(element => {
            if (!element) return;

            const hasJumpToPage = element.action === 'page' || element.value?.page_link;
            const hasLinkAndJump = element.value?.lj_page_link;

            if (hasJumpToPage || hasLinkAndJump) {
              const elementInfo = {
                elementId: element.id,
                elementType: element.element_type_name || 'Unknown',
                onPage: element.page || element.geometry?.page || 'Unknown',
                jumpToPage: hasLinkAndJump
                  ? element.value.lj_page_link
                  : (element.jump_to_page || element.value?.page_link || 'Unknown'),
                actionType: hasLinkAndJump ? 'Link & Jump' : 'Jump to Page',
                reactivateScrolling: element.value?.reactivate_scroll || false
              };
              jumpToPageElements.push(elementInfo);
            }
          });

          // Log results
          if (jumpToPageElements.length > 0) {
            // Group elements by page they're on
            const elementsByPage = jumpToPageElements.reduce((acc, elem) => {
              const page = elem.onPage;
              if (!acc[page]) acc[page] = [];
              acc[page].push(elem);
              return acc;
            }, {});

            const pagesWithJumps = Object.keys(elementsByPage).sort((a, b) => Number(a) - Number(b));

            // Also show all jump destinations
            const destinations = [...new Set(jumpToPageElements.map(e => e.jumpToPage))].sort((a, b) => {
              if (a === 'Unknown') return 1;
              if (b === 'Unknown') return -1;
              return Number(a) - Number(b);
            });

            console.log('📊 KDF Listing - Jump to Page Features Detected:', {
              kdfTitle: data?.title,
              kdfId: data?.id,
              isPublished: true,
              totalPages: data?.num_pages || 'Unknown',
              totalElements: elements.length,
              totalJumpElements: jumpToPageElements.length,
              pagesWithJumpFeatures: pagesWithJumps.join(', '),
              jumpDestinations: destinations.join(', '),
              summary: `${pagesWithJumps.length} of ${data?.num_pages || '?'} page(s) have Jump to Page elements`
            });
          } else {
            // Show which pages have any elements at all
            const pagesWithElements = [...new Set(
              elements
                .filter(e => e?.page || e?.geometry?.page)
                .map(e => e.page || e.geometry?.page)
            )].sort((a, b) => Number(a) - Number(b));

            console.log('📊 KDF Listing - No Jump to Page elements found:', {
              kdfTitle: data?.title,
              kdfId: data?.id,
              isPublished: true,
              totalPages: data?.num_pages || 'Unknown',
              totalElements: elements.length,
              pagesWithElements: pagesWithElements.join(', ') || 'None',
              message: 'This KDF has no Jump to Page features configured'
            });
          }
        }
      } catch (error) {
        console.warn('⚠️ Failed to fetch Jump to Page elements for KDF:', {
          kdfTitle: data?.title,
          kdfId: data?.id,
          error: error.message
        });
      }
    };

    if (data?.count_live_files > 0 && data?.id) {
      fetchAndLogJumpToPageElements();
    }
  }, [data?.id, data?.count_live_files, data?.title]);

  const canEditTags = !(
    data?.private_flag == true &&
    data?.user?.id != data?.current_user?.id
  );

  // Debounced so rapid successive add/remove clicks collapse into a single
  // PATCH (using the latest tag list) instead of racing several requests.
  const handleTagsChange = useCallback(
    (nextTags) => {
      setCurrentTags(nextTags);
      if (tagUpdateTimeoutRef.current) {
        clearTimeout(tagUpdateTimeoutRef.current);
      }
      tagUpdateTimeoutRef.current = setTimeout(() => {
        triggerUpdateRecordData(currentTagsRef.current, data, "tags");
      }, 600);
    },
    [data, triggerUpdateRecordData]
  );

  useEffect(() => {
    return () => {
      if (tagUpdateTimeoutRef.current) {
        clearTimeout(tagUpdateTimeoutRef.current);
      }
    };
  }, []);

  const handleAddLanguageClick = (event) => {
    event?.stopPropagation();
    window.location.href = `/kdfs/${data?.id}`;
  };

  const handlePDFStatusEventTrigger = async () => {
    let recordActionData;
    if (deletePDFActiveState != null) {
      recordActionData = await customAPIRequest(
        `docs/${data.id}/files/${deletePDFActiveState}.json`,
        "DELETE",
        {}
      );
      if (recordActionData?.data != undefined) {
        triggerUdateRecord();
      }
      setDeletePDFActiveState(null);
    } else if (privatePDFActiveState != null) {
      // handleUpdateDocData({"doc_id": docs.id,"private_flag": docUploadData.docData.private_flag});
      triggerUdateRecord();
    }
  };

  const handleUpdateEditableTitleEvent = (e) => {
    setEditableTitle((prev) => ({ ...prev, value: e.target.value }));
  };

  const handleOutsideOrEnter = useCallback(async () => {
    if (isSavingTitle) return;
    try {
      if (editableRef.current != null) {
        const newTitle = editableRef.current.value;
        setIsSavingTitle(true);
        const { status } = await networkService(
          `kdfs/${editableTitle?.id}.json`,
          "PATCH",
          { doc: { title: newTitle } }
        );
        if (status == 200) {
          setSavedTitle(newTitle);
          setEditableTitle((prev) => ({ ...prev, status: false, value: newTitle }));
          setShowNotifications({
            showToast: true,
            toastType: "success",
            toastMessage: "Changes Saved",
          });
        }
      }
    } catch (error) {
      if (error.response.status == 422) {
        setEditableTitle((prev) => ({
          ...prev,
          status: false,
          value: savedTitle,
        }));
        setShowNotifications({
          showToast: true,
          toastType: "danger",
          toastMessage:
            "Sorry, KDF titles must be unique. This title already exists in your KDF library.",
        });
      }
    } finally {
      setIsSavingTitle(false);
    }
  }, [isSavingTitle, editableTitle?.id, savedTitle, setShowNotifications]);

  const handleStartEditTitle = () => {
    setEditableTitle((prev) => ({ ...prev, status: true, value: savedTitle }));
  };

  const handleCancelTitleEdit = useCallback(() => {
    setEditableTitle((prev) => ({ ...prev, status: false, value: savedTitle }));
  }, [savedTitle]);

  useEffect(() => {
    if (!editableTitle.status) return;

    const handleClickOutside = (event) => {
      const scope = editGroupRef.current || editableRef.current;
      if (scope && !scope.contains(event.target)) {
        handleOutsideOrEnter();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Enter") {
        handleOutsideOrEnter();
      } else if (event.key === "Escape") {
        handleCancelTitleEdit();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [editableTitle.status, handleOutsideOrEnter, handleCancelTitleEdit]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div key={forKey}>
      <LaserLinkModal
        laserItem={laserLINKEditStatus}
        time_zone_list={time_zone_list}
        docData={data}
        triggerCancelProcess={setLaserLINKEditStatus}
        onSaved={() => triggerUdateRecord({ silent: true })}
        batchStatus={laserLINKBatchStatus}
      />
      <Row
        className={`${styles.listWrapper} ${
          !data?.count_live_files > 0 ? styles.unPublished : ""
        } ${
          data?.private_flag == true && data?.user?.id != data?.current_user?.id
            ? styles.privateKDF
            : ""
        } gx-0`}
      >
        <Col className="text-center" md={2}>
         <a
             href={data?.viewer_dev}
             className={`${styles.thumbImageContainer} ${
               data?.private_flag == true && data?.user?.id == data?.current_user?.id
                 ? styles.privateOwnerThumb
                 : ""
             }`}
         >
          <img
            src={
              data?.private_flag == true &&
              data?.user?.id != data?.current_user?.id
                ? privateKdf
                : isMobile && data.thumb_mobile && data.thumb_mobile != ""
                ? data.thumb_mobile
                : data.thumb != ""
                ? data.thumb
                : noImage
            }
            alt={data.title}
            className={`${styles.thumbImage} ${
              (isMobile && data.thumb_mobile && data.thumb_mobile != "") || 
              (!isMobile && data.thumb != "")
                ? "" 
                : styles.noThumbImage
            } w-70`}
          />
         </a>
          <Button
            icon={ <svg
    xmlns="http://www.w3.org/2000/svg"
    width="21"
    height="20"
    fill="none"
    viewBox="0 0 21 20"
  >
    <mask
      id="mask0_10_75"
      width="21"
      height="20"
      x="0"
      y="0"
      maskUnits="userSpaceOnUse"
      style={{ maskType: "alpha" }}
    >
      <path fill="#D9D9D9" d="M.5 0h20v20H.5z"></path>
    </mask>
    <g mask="url(#mask0_10_75)">
      <path
        fill="#fff"
        d="M4.667 15.833h1.187L14 7.688 12.813 6.5l-8.146 8.146zM3 17.5v-3.542L14 2.98q.25-.229.552-.354t.636-.125.645.125q.314.125.542.375l1.146 1.167q.25.229.364.541a1.8 1.8 0 0 1 0 1.26 1.6 1.6 0 0 1-.364.553L6.54 17.5zM13.396 7.104l-.584-.604L14 7.688z"
      ></path>
    </g>
  </svg>}
            text={"Edit KDF"}
            classList={`${
              data?.count_active_files !== 0 ? "success" : "disabled"
            } full-width`}
            addonClass={"mt-2"}
            onClick={() => {
              if (!(data?.private_flag == true && data?.user?.id != data?.current_user?.id)) {
                window.open(`/editor/${data?.id}`, `editor-${data?.id}`);
              }
            }}
            disabled={data?.count_active_files == 0}
            // Green while unpublished: editing/publishing is the next step (Create LaserLINK is the outlined one then)
            variant={data?.count_live_files == 0 && data?.count_active_files != 0 ? "green" : "blue"}
          />
          {/* <div className="kdf-a">
            <a target="_blank" href={`/kdfs/${data?.id}`}>
              KDF Settings
            </a>
          </div> */}
        </Col>
        <Col md={10} className={styles.docWrapper}>
          <Row>
            <Col md={8} xs={12}>
              <div className={styles.docTitle}>
                {data?.private_flag == true &&
                data?.user?.id != data?.current_user?.id ? (
                  <CustomTooltip
                    title={
                      <p className="p-1 text-light">This is Private KDF</p>
                    }
                  >
                    <strong>Confidential... PRIVATE KDF</strong>
                  </CustomTooltip>
                ) : editableTitle.status ? (
                  <span className={styles.titleEditGroup} ref={editGroupRef}>
                    <input
                      type="text"
                      className={styles.titleInput}
                      value={editableTitle?.value}
                      ref={editableRef}
                      onChange={(e) => handleUpdateEditableTitleEvent(e)}
                      maxLength={40}
                      disabled={isSavingTitle}
                      autoFocus
                    />
                    <button
                      type="button"
                      className={styles.titleActionBtn}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={handleOutsideOrEnter}
                      disabled={isSavingTitle}
                      aria-label="Save title"
                    >
                      <FontAwesomeIcon icon={faCheck} />
                    </button>
                    <button
                      type="button"
                      className={`${styles.titleActionBtn} ${styles.titleActionBtnCancel}`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={handleCancelTitleEdit}
                      disabled={isSavingTitle}
                      aria-label="Cancel"
                    >
                      <FontAwesomeIcon icon={faRectangleXmark} />
                    </button>
                  </span>
                ) : (
                  <span className={styles.titleDisplayGroup}>
                    <p onClick={handleStartEditTitle}>{savedTitle}</p>
                    <button
                      type="button"
                      className={styles.titleEditPencil}
                      onClick={handleStartEditTitle}
                      aria-label="Edit title"
                    >
                      <FontAwesomeIcon icon={faPen} />
                    </button>
                  </span>
                )}
                {data?.private_flag == true && (
                  <span className={styles.privatePill} aria-label="Private KDF">
                    <FontAwesomeIcon icon={faLock} className={styles.privatePillLock} />
                    <span className={styles.privatePillText}>Private</span>
                  </span>
                )}
              </div>
              <p className={styles.docText}>
                <CustomTooltip
                    title={
                      <p className="p-1 text-light">Modified on {data.date_updated} by {data.modifier? data.modifier :data.user?.name }</p>
                    } >
                <span>
                  Created by:{" "}
                  <span className={styles.createdBy}>
                    {" "}
                    {data.owner_name || data.user?.name || " "}
                  </span>{" "}
                  {data.owner_email && <span>({data.owner_email})</span>}
                </span>
               </CustomTooltip>
              </p>
              {/* <p className={styles.docText}>
                <span
                  className={`ml-1 ${
                    data.private_permission ? "" : "blur_items"
                  }`}
                >
                  Total Pages:&nbsp;
                  <span className={styles.pagesCount}>
                    {data.num_pages === 1
                      ? `${data.num_pages} Page `
                      : `${data.num_pages ?? 0} Pages `}
                  </span>
                </span>
              </p> */}

              <p className={styles.docText}>
                <span>
                  Engagement Threshold:{" "}
                  <span className={styles.totalTime}>
                    {data.page_viewed_sec} seconds
                  </span>
                </span>
              </p>
              <p className="d-flex align-items-center kdf-tag-n">
                <span id="kdf-tags" className={styles.docText}>
                  {(currentTags?.length > 0 || canEditTags) && (
                    <>
                      Tags:{" "}
                      <TagChipEditor
                        tags={currentTags}
                        onChange={handleTagsChange}
                        canEdit={canEditTags}
                        limit={MAX_TAGS}
                        placeholderText="Add a tag..."
                        collapsible
                      />
                    </>
                  )}
                </span>
              </p>
              <p className={[styles.docText, "add-language-wrapper"].join(" ")}>
                {data.doc_files_languages && data.doc_files_languages.length > 0 ? (
                  <>
                  <span> Languages:&nbsp;</span>
                    {data.doc_files_languages.map((lanDoc, lanInd) => (
                      <span className={[styles.lanFlag, "d-flex"].join(" ")} key={lanInd}>
                        <span
                          className={lanDoc?.language?.class}
                          style={{
                            width: "22px",
                            borderRadius: "2px",
                            height: "16px",
                            marginRight: "0",
                          }}
                        ></span>
                      </span>
                    ))}
                  </>
                ) : (
                  <span
                    className="custom-tag-element mb-0 d-flex"
                    style={{ gap: "3px" }}
                    onClick={handleAddLanguageClick}
                  >
                    <span className="d-flex">
                      <img src="/icons/add_2.svg" width={14} />
                    </span>
                    Add Language
                  </span>
                )}
              </p>
            </Col>
            <Col md={4} xs={12}>
              <div className={styles.createlinkContainer}>
                <div className={`createLaserbtn ${styles.kdfStatus}`}>
                  <Button
                    text={"Create LaserLINK"}
                    classList={`${
                      data?.count_live_files == 0 ? "outline" : "success"
                    }`}
                    addonClass={""}
                    onClick={() =>
                      data?.count_live_files > 0
                        ? setLaserLINKEditStatus(true)
                        : setShowNotifications({
                            showToast: true,
                            toastType: "danger",
                            toastMessage:
                              "This KDF is not yet published, Therefore it can not be shared.",
                          })
                    }
                  />
                  {/* <button>
              <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                  >
                  <mask
                    id="mask0_14_101"
                    width="24"
                    height="24"
                    x="0"
                    y="0"
                    maskUnits="userSpaceOnUse"
                    style={{ maskType: "alpha" }}
                  >
                    <path fill="#D9D9D9" d="M0 0h24v24H0z"></path>
                  </mask>
                  <g mask="url(#mask0_14_101)">
                    <path fill="#fff" d="M11 13H5v-2h6V5h2v6h6v2h-6v6h-2z"></path>
                  </g>
                </svg>
              &nbsp;Create LaserLink
              </button> */}
                  <span>
                    {data.count_live_files > 0 && !data.private_flag ? (
                      <CustomTooltip
                        title={
                          <p className="p-1 text-light">
                            {data?.published_status || data.count_live_files > 0
                              ? "Published"
                              : "Unpublished"}{" "}
                            on {data.doc_date_updated} by {data.username}
                          </p>
                        }
                      >
                        <span className={styles.statusPublished}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            fill="none"
                            viewBox="0 0 20 20"
                          >
                            <mask
                              id="mask0_14_157"
                              width="20"
                              height="20"
                              x="0"
                              y="0"
                              maskUnits="userSpaceOnUse"
                              style={{ maskType: "alpha" }}
                            >
                              <path fill="#D9D9D9" d="M0 0h20v20H0z"></path>
                            </mask>
                            <g mask="url(#mask0_14_157)">
                              <path
                                fill="#34C759"
                                d="m8.833 13.833 5.875-5.875-1.166-1.166L8.833 11.5 6.458 9.125l-1.166 1.167zm1.167 4.5a8.1 8.1 0 0 1-3.25-.656 8.4 8.4 0 0 1-2.646-1.781 8.4 8.4 0 0 1-1.781-2.646A8.1 8.1 0 0 1 1.667 10q0-1.73.656-3.25a8.4 8.4 0 0 1 1.781-2.646A8.4 8.4 0 0 1 6.75 2.323 8.1 8.1 0 0 1 10 1.667q1.73 0 3.25.656a8.4 8.4 0 0 1 2.646 1.781 8.4 8.4 0 0 1 1.781 2.646 8.1 8.1 0 0 1 .656 3.25 8.1 8.1 0 0 1-.656 3.25 8.4 8.4 0 0 1-1.781 2.646 8.4 8.4 0 0 1-2.646 1.781 8.1 8.1 0 0 1-3.25.656m0-1.666q2.792 0 4.73-1.938 1.936-1.937 1.937-4.729 0-2.791-1.938-4.73Q12.792 3.335 10 3.334q-2.792 0-4.73 1.938Q3.335 7.208 3.334 10q0 2.79 1.938 4.728T10 16.667"
                              ></path>
                            </g>
                          </svg>
                          {data.count_live_files > 0
                            ? "Published"
                            : "Unpublished"}
                        </span>
                      </CustomTooltip>
                    ) : data.count_live_files > 0 ? (
                      "Published"
                    ) : (
                      "Unpublished"
                  )}
                  <span className={styles.pagesCount}>
                    (
                    {data.num_pages === 1
                      ? `${data.num_pages} Page `
                      : `${data.num_pages ?? 0} Pages `}
                      )
                  </span>
                  </span>
                </div>
                <Dropdown className="kdfActions">
                  <Dropdown.Toggle variant="success" id="dropdown-basic">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="9"
                      height="26"
                      fill="none"
                      viewBox="0 0 9 29"
                    >
                      <circle
                        cx="4.5"
                        cy="3.5"
                        r="3.5"
                        fill="#333"
                        transform="rotate(90 4.5 3.5)"
                      ></circle>
                      <circle
                        cx="4.5"
                        cy="13.5"
                        r="3.5"
                        fill="#333"
                        transform="rotate(90 4.5 13.5)"
                      ></circle>
                      <circle
                        cx="4.5"
                        cy="23.5"
                        r="3.5"
                        fill="#333"
                        transform="rotate(90 4.5 23.5)"
                      ></circle>
                    </svg>
                  </Dropdown.Toggle>

                  <Dropdown.Menu>
                    <Dropdown.Item href={`/kdfs/${data?.id}`}>
                      KDF Settings
                    </Dropdown.Item>
                    {totalLaserLinks > 0 && (
                      <Dropdown.Item
                        href={`/links/doc/${data?.group_id}?filter=0`}
                      >
                        {laserLinkLabel} ({totalLaserLinks})
                      </Dropdown.Item>
                    )}
                    
                    <Dropdown.Item>
                      <span
                        className="text-link cursor-pointer d-block"
                        onClick={() =>
                          data?.private_flag == true &&
                          data?.user?.id != data?.current_user?.id
                            ? ""
                            : triggerDeleteKDF(data.id)
                        }
                      >
                        Delete this KDF
                      </span>
                    </Dropdown.Item>
                    
                    <Dropdown.Item>
                      <span
                        className="text-link cursor-pointer d-block"
                        onClick={() =>
                          data?.private_flag == true &&
                          data?.user?.id != data?.current_user?.id
                            ? ""
                            : triggerCloneKDF(data.id)
                        }
                      >
                        Clone this KDF
                      </span>
                    </Dropdown.Item>

   
                      {data?.user?.id == data?.current_user?.id && (
                      <Dropdown.Item>
                        <CustomTooltip
                          title={
                            <p className="p-1 text-light">
                              Prevent other Kinetizine users from viewing and
                              editing this KDF
                            </p>
                          }
                        >
                          <span
                            className="text-link cursor-pointer d-block"
                            onClick={() =>
                              data?.private_flag == true &&
                              data?.user?.id != data?.current_user?.id
                                ? ""
                                : triggerMakePrivateEvent({
                                    id: data.id,
                                    status: data?.private_flag,
                                  })
                            }
                          >
                            Make{" "}
                            {data?.private_flag == true ? "Public" : "Private"}
                          </span>
                        </CustomTooltip>
                        </Dropdown.Item>
                      )}

                    <Dropdown.Item>
                      <CustomTooltip
                        title={
                          <p className="p-1 text-light">
                            Generate Multiple LaserLINX using a spreadsheet
                          </p>
                        }
                      >
                        <a
                          className="link-success"
                          onClick={() =>
                            data?.private_flag == true &&
                            data?.user?.id != data?.current_user?.id
                              ? ""
                              : data?.count_live_files > 0 &&
                                (setLaserLINKEditStatus(true),
                                setLaserLINKBatchStatus(true))
                          }
                        >
                          Gen. MultiLINX
                        </a>
                      </CustomTooltip>
                    </Dropdown.Item>

                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </Col>
          </Row>

          <Row>
            <Col md={12} className="pl-0">
              {parseInt(data.stats.viewers) > 0 && (
                <div className={`kdf-list-pages-wrapper ${isStatsCollapsed ? 'collapsed' : 'expanded'}`}>
                <div className="kdf-list-pages">
                  <CustomTooltip
                    title={
                      <div
                        className="p-2"
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHtml(data?.view_stats ?? ""),
                        }}
                      />
                    }
                  >
                    <a
                      href={`/kdf-stats/${data?.group_id}#/views`}
                      className={`${styles.statsLink} my-1 me-1 text-center custom-link-success`}
                    >
                      <strong className="kdf-score-l">
                        {data.stats.viewers}
                      </strong>
                      <span>Visits</span>
                    </a>
                  </CustomTooltip>
                  <a href={`/kdf-stats/${data?.group_id}#/at-a-glance`} className={`${styles.statsLink} my-1 mx-2 text-center custom-link-success`}>
                    <CustomTooltip title={
                      data?.stats?.duration_breakdown ? (
                        data.stats.duration_breakdown.count >= 2 ? (
                          <div className="p-1 text-light">
                            <div>Median: {data.stats.duration_breakdown.median}</div>
                            <div>Average: {data.stats.duration_breakdown.avg}</div>
                            <div>Max: {data.stats.duration_breakdown.max}</div>
                          </div>
                        ) : (
                          <div className="p-1 text-light">
                            <div>Duration: {data.stats.duration_breakdown.max}</div>
                          </div>
                        )
                      ) : null
                    }>
                      <strong className="kdf-score-l">{data.stats.min_viewed}</strong>
                    </CustomTooltip>
                    <CustomTooltip placement={"bottom"} title={<p className="p-1 text-light">{data?.stats?.min_viewed_tooltip}</p>}>
                      <span>Duration</span>
                    </CustomTooltip>
                  </a>
                  <a
                    href={`/kdf-stats/${data?.group_id}#/at-a-glance`}
                    className={`${styles.statsLink} my-1 mx-2 text-center custom-link-success`}
                  >
                    <strong className="kdf-score-l">
                      {data.stats.percent_viewed}
                    </strong>
                    <span>Viewed</span>
                  </a>
                  <CustomTooltip
                    title={
                      <div className="p-1 text-light">
                        {parseInt(data?.stats?.optin_breakdown?.optins, 10) > 0 && (
                          <div>{data.stats.optin_breakdown.optins} Optin</div>
                        )}
                        {parseInt(data?.stats?.optin_breakdown?.multilink_optins, 10) > 0 && (
                          <div>{data.stats.optin_breakdown.multilink_optins} MultiLink Campaign</div>
                        )}
                        <div>{data?.opt_in_reject} Bounced</div>
                      </div>
                    }
                  >
                    <a
                      href={`/kdf-stats/${data?.group_id}#/viewers`}
                      className={`${styles.statsLink} my-1 mx-2 text-center custom-link-success`}
                    >
                      <strong className="kdf-score-l">
                        {data.stats.unique_viewers}
                      </strong>
                      <span>Opt-in</span>
                    </a>
                  </CustomTooltip>
                  <a
                    href={`/kdf-stats/${data?.group_id}#/views`}
                    className={`${styles.statsLink} my-1 mx-2 text-center custom-link-success`}
                  >
                    <strong className="kdf-score-l">
                      {data.stats.engagement?.replace("%", "")}
                    </strong>
                    <span>Score</span>
                  </a>
                  <a
                    href={`/kdf-stats/${data?.group_id}#/at-a-glance`}
                    className={`${styles.statsLink} my-1 me-1 text-center custom-link-success`}
                  >
                    <strong className="kdf-score-l">
                      {data.stats.last_viewed}
                    </strong>
                    <span>Last Viewed</span>
                  </a>
                </div>
                  <button 
                    type="button"
                    className="kdf-stats-toggle-mobile"
                    onClick={() => setIsStatsCollapsed(!isStatsCollapsed)}
                    aria-expanded={!isStatsCollapsed}
                  >
                    <span>{isStatsCollapsed ? 'View Stats' : 'View Less'}</span>
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="12" 
                      height="8" 
                      viewBox="0 0 12 8" 
                      fill="none"
                      className={isStatsCollapsed ? '' : 'rotated'}
                    >
                      <path d="M1 1L6 6L11 1" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              )}
            </Col>
          </Row>
        </Col>
        {/* <Col md={2}> */}
        {/* {data.doc_files_languages?.map((lanDoc, lanInd) => (
                <div key={lanInd}>
                  <div className={lanDoc?.language?.class} style={{
                    width: "22px",
                  }}></div>
                </div>
              ))} */}
        {/* </Col> */}

        {/* <Col md={3} className="LASER-CREATE">
          <Button
            text={"Create Laser LINK"}
            classList={`${data?.count_live_files == 0 ? "outline" : "success"}`}
            addonClass={"mb-2"}
            onClick={() =>
              data?.count_live_files > 0 ? setLaserLINKEditStatus(true) : setShowNotifications({
                showToast: true,
                toastType: "danger",
                toastMessage: 'This KDF is not yet published, Therefore it can not be shared.',
              })
            }
          />
          {
            data?.tracking_link_count > 0 &&
            <a href={`/links/doc/${data?.group_id}?filter=0`}>LaserLINK ({data?.tracking_link_count})</a>
          }
          <div className="laser-popups mt-1">

            <span
              className="text-link cursor-pointer d-block"
              onClick={() => data?.private_flag == true && data?.user?.id != data?.current_user?.id ? '' : (triggerDeleteKDF(data.id))}
            >
              Delete this KDF
            </span>
            <CustomTooltip title={<p className="p-1 text-light">Clone KDF</p>}>
              <span
                className="text-link cursor-pointer d-block"
                onClick={() => data?.private_flag == true && data?.user?.id != data?.current_user?.id ? '' : (triggerCloneKDF(data.id))}
              >
                Clone this KDF
              </span>
            </CustomTooltip>
            {
              data?.user?.id == data?.current_user?.id &&
              <CustomTooltip
                title={
                  <p className="p-1 text-light">Prevent other Kinetizine users from viewing and editing this KDF</p>
                }
              >
                <span
                  className="text-link cursor-pointer d-block"
                  onClick={() =>
                    data?.private_flag == true && data?.user?.id != data?.current_user?.id ? '' : (triggerMakePrivateEvent({
                      id: data.id,
                      status: data?.private_flag,
                    }))
                  }
                >
                  Make {data?.private_flag == true ? "Public" : "Private"}
                </span>
              </CustomTooltip>
            }
            <CustomTooltip
              title={
                <p className="p-1 text-light">Generate Multiple LaserLINX using a spreadsheet</p>
              }
            >
              <a
                className="link-success"
                onClick={() =>
                  data?.private_flag == true && data?.user?.id != data?.current_user?.id ? '' : (data?.count_live_files > 0 &&
                    (setLaserLINKEditStatus(true),
                      setLaserLINKBatchStatus(true)))
                }
              >
                Gen. MultiLINX
              </a>
            </CustomTooltip>
          </div>
        </Col> */}
      </Row>
    </div>
  );
});
export default KdfListCard;
